import { useState, useEffect } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Plus, Power, PowerOff, Edit, Trash2, AlertTriangle, Info, AlertCircle } from 'lucide-react'
import { AlertWizard } from './AlertWizard'
import type { AlertRule } from '@/types/fleetvision'

export function AlertRulesManager() {
  const [rules, setRules] = useState<AlertRule[]>([])
  const [loading, setLoading] = useState(true)
  const [wizardOpen, setWizardOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null)

  useEffect(() => {
    fetchRules()
  }, [])

  const fetchRules = async () => {
    try {
      const response = await fetch('/api/alert-rules')
      const data = await response.json()
      setRules(data)
    } catch (error) {
      console.error('Failed to fetch alert rules:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleRule = async (ruleId: string) => {
    try {
      const response = await fetch(`/api/alert-rules/${ruleId}/toggle`, {
        method: 'PATCH',
      })
      const data = await response.json()

      if (data.success) {
        setRules(rules.map(rule =>
          rule.id === ruleId ? { ...rule, enabled: data.enabled } : rule
        ))
      }
    } catch (error) {
      console.error('Failed to toggle rule:', error)
    }
  }

  const handleDeleteRule = async (ruleId: string) => {
    if (!confirm('Are you sure you want to delete this alert rule?')) {
      return
    }

    try {
      const response = await fetch(`/api/alert-rules/${ruleId}`, {
        method: 'DELETE',
      })
      const data = await response.json()

      if (data.success) {
        setRules(rules.filter(rule => rule.id !== ruleId))
      }
    } catch (error) {
      console.error('Failed to delete rule:', error)
    }
  }

  const handleEditRule = (rule: AlertRule) => {
    setEditingRule(rule)
    setWizardOpen(true)
  }

  const handleWizardClose = () => {
    setWizardOpen(false)
    setEditingRule(null)
    fetchRules()
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="h-4 w-4 text-red-500" />
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />
      case 'info':
        return <Info className="h-4 w-4 text-blue-500" />
      default:
        return null
    }
  }

  const getSeverityBadge = (severity: string) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      info: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    }
    return (
      <Badge variant="secondary" className={colors[severity as keyof typeof colors]}>
        {severity}
      </Badge>
    )
  }

  const getConditionSummary = (condition: any): string => {
    switch (condition.kind) {
      case 'metric_threshold':
        return `${condition.metric} ${condition.operator} ${condition.value} (${condition.aggregation})`
      case 'geofence_event':
        const geofences = condition.geofenceIds?.length ? `${condition.geofenceIds.length} geofence(s)` : 'any geofence'
        return `Geofence ${condition.direction}: ${geofences}`
      case 'hos_threshold':
        return `${condition.hosMetric} ${condition.operator} ${condition.value}h`
      case 'logical':
        return `${condition.op} of ${condition.children.length} conditions`
      default:
        return 'Custom condition'
    }
  }

  if (loading) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground">Loading alert rules...</p>
      </Card>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Alert Rules</h2>
            <p className="text-muted-foreground mt-1">
              Configure intelligent alerts for your fleet with custom conditions and actions
            </p>
          </div>
          <Button onClick={() => setWizardOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Alert Rule
          </Button>
        </div>

        {/* Rules List */}
        {rules.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="rounded-full bg-muted p-3">
                <AlertTriangle className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-medium">No alert rules configured</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Get started by creating your first alert rule
                </p>
              </div>
              <Button onClick={() => setWizardOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Rule
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {rules.map((rule) => (
              <Card key={rule.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getSeverityIcon(rule.severity)}
                      <h3 className="text-lg font-medium">{rule.name}</h3>
                      {getSeverityBadge(rule.severity)}
                      <Badge variant={rule.enabled ? 'default' : 'secondary'}>
                        {rule.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>

                    {rule.description && (
                      <p className="text-sm text-muted-foreground mb-3">
                        {rule.description}
                      </p>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Scope:</span>{' '}
                        <span className="font-medium capitalize">{rule.scope_type}</span>
                        {rule.scope_selector?.ids?.length && (
                          <span className="text-muted-foreground"> ({rule.scope_selector.ids.length} items)</span>
                        )}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Condition:</span>{' '}
                        <span className="font-medium">{getConditionSummary(rule.condition)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Channels:</span>{' '}
                        <span className="font-medium">{rule.channels.length} configured</span>
                      </div>
                    </div>

                    <div className="flex gap-2 mt-4 text-xs text-muted-foreground">
                      <span>Eval window: {rule.evaluation_window_sec}s</span>
                      <span>•</span>
                      <span>Frequency: {rule.frequency_sec}s</span>
                      {rule.suppression_window_sec && (
                        <>
                          <span>•</span>
                          <span>Suppression: {rule.suppression_window_sec}s</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleRule(rule.id)}
                      title={rule.enabled ? 'Disable rule' : 'Enable rule'}
                    >
                      {rule.enabled ? (
                        <Power className="h-4 w-4 text-green-600" />
                      ) : (
                        <PowerOff className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditRule(rule)}
                      title="Edit rule"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteRule(rule.id)}
                      title="Delete rule"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Alert Wizard Dialog */}
      <AlertWizard
        open={wizardOpen}
        onClose={handleWizardClose}
        editingRule={editingRule}
      />
    </>
  )
}
